import React, { useState, useEffect, useCallback } from 'react';
import { Box, IconButton } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import GlassCard from './GlassCard';
import Text from '../Text';
import { colors, radii } from '../../theme/designTokens';

const TestimonialCarousel = ({ items, autoSlideMs = 5000 }) => {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);

  const goTo = useCallback(
    (index) => {
      setDirection(index > current ? 1 : -1);
      setCurrent(index);
    },
    [current]
  );

  const next = useCallback(() => {
    setDirection(1);
    setCurrent((prev) => (prev + 1) % items.length);
  }, [items.length]);

  const prev = useCallback(() => {
    setDirection(-1);
    setCurrent((prev) => (prev - 1 + items.length) % items.length);
  }, [items.length]);

  useEffect(() => {
    const timer = setInterval(next, autoSlideMs);
    return () => clearInterval(timer);
  }, [next, autoSlideMs]);

  const slideVariants = {
    enter: (dir) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -60 : 60, opacity: 0 }),
  };

  const item = items[current];

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', position: 'relative' }}>
      <Box sx={{ position: 'relative', minHeight: 220, overflow: 'hidden' }}>
        <AnimatePresence custom={direction} mode="wait">
          <Box
            key={current}
            component={motion.div}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <GlassCard variant="light" sx={{ p: { xs: 3, md: 4 } }}>
              <FormatQuoteIcon sx={{ color: colors.silverDark, fontSize: 40, mb: 1.5, opacity: 0.8 }} />
              <Text variant="quote" sx={{ mb: 2.5, display: 'block' }}>
                {item.quote}
              </Text>
              <Text variant="h4" sx={{ display: 'block' }}>{item.author}</Text>
              <Text variant="caption" color="secondary" sx={{ mt: 0.25, display: 'block' }}>
                {item.role}
              </Text>
            </GlassCard>
          </Box>
        </AnimatePresence>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 3 }}>
        <IconButton onClick={prev} size="small" sx={{ color: colors.navy }}>
          <ArrowBackIosNewIcon fontSize="small" />
        </IconButton>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {items.map((_, i) => (
            <Box
              key={i}
              component="button"
              onClick={() => goTo(i)}
              aria-label={`Go to testimonial ${i + 1}`}
              sx={{
                width: current === i ? 24 : 8,
                height: 8,
                borderRadius: radii.pill,
                border: 'none',
                cursor: 'pointer',
                background: current === i ? colors.blue : colors.border,
                transition: 'all 0.3s ease',
                p: 0,
              }}
            />
          ))}
        </Box>
        <IconButton onClick={next} size="small" sx={{ color: colors.navy }}>
          <ArrowForwardIosIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default TestimonialCarousel;
